import * as React from 'react';
import type {
  EntityDecision,
  LinkData,
  ResolvedEntity
} from 'shared/src/api/ai-media-plan-chat-types';
import {
  CampaignGeoTargets,
  CreateAdGroupPayload,
  CreateCampaignBudgetPayload,
  CreateCampaignPayload
} from 'shared/src/google-ads/google-ads-types';
import { buildGoogleAdsEntityUrl } from 'shared/src/domain/ai/skills/google-ads/shared-config';
import {
  extractPartialFailureDetail,
  formatPartialFailureMessage
} from 'shared/src/api/partial-failure';
import { Button } from '@/components/forms/ui/button';
import { Input } from '@/components/forms/ui/input';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import type { MediaPlanPreviewCardProps } from '../preview-card-registry';
import { ProposalSummarySection } from '../proposal-summary-section';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';

export function GoogleAdsMediaPlanPreviewCard({
  data,
  strategyId,
  lineItemName,
  onFieldsEdited,
  onCreated,
  onCreatedLinks,
  nextPlatform,
  onRequestNewPlatform,
  toolCallId,
  externalOutcome,
  updatedAfterResolve,
  onResolve
}: MediaPlanPreviewCardProps): JSX.Element {
  // Confirmed-write transport: when a toolCallId is present the card resolves the
  // createGoogleAdsMediaPlan tool via the googleAdsConfirmMediaPlan endpoint; otherwise it uses the
  // legacy direct confirmMediaPlanCreate mutation.
  const isConfirmedWrite = Boolean(toolCallId);
  // Group the flat entities into [campaign_budget, campaign, ad_group] triples — one per Google Ads
  // campaign. The common case is a single triple (fully editable); a multi-campaign split renders
  // each group read-only (edits go back through chat to re-resolve the split).
  const triples = toTriples(data.entities);
  const isMultiCampaign = triples.length > 1;
  const budgetDecision = findEntity(data.entities, 'campaign_budget');
  const campaignDecision = findEntity(data.entities, 'campaign');
  const adGroupDecision = findEntity(data.entities, 'ad_group');

  const isExistingCampaign = campaignDecision?.mode === 'existing';
  const existingCampaignId =
    campaignDecision?.mode === 'existing' ? campaignDecision.entity_id : null;
  const existingBudgetId = budgetDecision?.mode === 'existing' ? budgetDecision.entity_id : null;

  const initialBudget =
    budgetDecision?.mode === 'create'
      ? (budgetDecision.payload as CreateCampaignBudgetPayload)
      : null;
  const initialCampaign =
    campaignDecision?.mode === 'create'
      ? (campaignDecision.payload as CreateCampaignPayload)
      : null;
  const initialAdGroup =
    adGroupDecision?.mode === 'create' ? (adGroupDecision.payload as CreateAdGroupPayload) : null;

  const [budgetPayload, setBudgetPayload] = React.useState<CreateCampaignBudgetPayload | null>(
    initialBudget ? { ...initialBudget } : null
  );
  const [campaignPayload, setCampaignPayload] = React.useState<CreateCampaignPayload | null>(
    initialCampaign ? { ...initialCampaign } : null
  );
  const [adGroupPayload, setAdGroupPayload] = React.useState<CreateAdGroupPayload | null>(
    initialAdGroup ? { ...initialAdGroup } : null
  );

  const initialBudgetRef = React.useRef(initialBudget ? { ...initialBudget } : null);
  const initialCampaignRef = React.useRef(initialCampaign ? { ...initialCampaign } : null);
  const initialAdGroupRef = React.useRef(initialAdGroup ? { ...initialAdGroup } : null);

  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);
  const [campaignSuccessLinks, setCampaignSuccessLinks] = React.useState<LinkData[]>([]);
  // Non-terminal errors (legacy-transport failures, missing-payload guards): card stays actionable.
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [createWarnings, setCreateWarnings] = React.useState<string[]>([]);
  const [notCreated, setNotCreated] = React.useState<string[]>([]);
  const [editing, setEditing] = React.useState(false);

  const legacyMutation = trpc.confirmMediaPlanCreate.useMutation({
    onSuccess: result => {
      setOutcome({ status: 'applied', summary: 'Media plan created in Google Ads' });
      setEditing(false);
      setCreateWarnings(result.warnings ?? []);
      setNotCreated(result.not_created ?? []);
      const links = campaignLinksFromResolved(result.resolved_entities);
      setCampaignSuccessLinks(links);
      onCreated?.();
      onCreatedLinks?.(links);
    },
    onError: err => {
      const partial = extractPartialFailureDetail(err);
      const msg = partial ? formatPartialFailureMessage(partial) : err.message;
      setErrorMsg(msg);
      showErrorToast(msg);
    }
  });

  const confirmedWriteMutation = trpc.googleAdsConfirmMediaPlan.useMutation();

  const isPending = legacyMutation.isPending || confirmedWriteMutation.isPending;

  function buildEntities(): EntityDecision[] | null {
    if (!adGroupPayload) {
      setErrorMsg('Preview is missing the ad_group payload.');
      return null;
    }
    if (isExistingCampaign) {
      if (!existingBudgetId || !existingCampaignId) {
        setErrorMsg('Preview is missing the existing campaign or budget reference.');
        return null;
      }
      return [
        { mode: 'existing', entityType: 'campaign_budget', entity_id: existingBudgetId },
        { mode: 'existing', entityType: 'campaign', entity_id: existingCampaignId },
        { mode: 'create', entityType: 'ad_group', payload: adGroupPayload }
      ];
    }
    if (!budgetPayload || !campaignPayload) {
      setErrorMsg('Preview is missing one of: campaign_budget, campaign payload.');
      return null;
    }
    return [
      { mode: 'create', entityType: 'campaign_budget', payload: budgetPayload },
      { mode: 'create', entityType: 'campaign', payload: campaignPayload },
      { mode: 'create', entityType: 'ad_group', payload: adGroupPayload }
    ];
  }

  async function handleCreate() {
    setErrorMsg(null);
    // A multi-campaign split is created exactly as resolved by proposeGoogleAds (read-only on the
    // card); the single-campaign path rebuilds entities from the editable payloads.
    const entities = isMultiCampaign ? data.entities : buildEntities();
    if (!entities) return;

    if (isConfirmedWrite && toolCallId && onResolve) {
      try {
        const res = await confirmedWriteMutation.mutateAsync({
          execution_line_item_id: data.execution_line_item_id,
          strategy_id: strategyId,
          platform_id: data.platform_id,
          tool_call_id: toolCallId,
          account_id: data.account_id ?? '',
          manager_account_id: data.manager_account_id,
          entities
        });
        setOutcome({ status: 'applied', summary: 'Media plan created in Google Ads' });
        setEditing(false);
        setCreateWarnings(res.result?.warnings ?? []);
        setNotCreated(res.result?.not_created ?? []);
        const links = campaignLinksFromResolved(res.result?.resolved_entities ?? []);
        setCampaignSuccessLinks(links);
        onCreated?.();
        onCreatedLinks?.(links);
        await onResolve({
          status: res.status,
          media_plans: res.result?.media_plans,
          resolved_entities: res.result?.resolved_entities,
          warnings: res.result?.warnings,
          not_created: res.result?.not_created
        });
      } catch (err) {
        const partial = extractPartialFailureDetail(err);
        const msg = partial
          ? formatPartialFailureMessage(partial)
          : err instanceof Error
            ? err.message
            : 'Failed to create media plan';
        setOutcome({ status: 'failed', summary: msg });
        showErrorToast(msg);
        // Feed the error back to the agent so it can suggest a remediation and re-propose a corrected
        // card (the user isn't stranded — a fresh card replaces this one). The agent is prompted to fix
        // fixable inputs and re-propose once, or relay guidance for partial/system failures.
        await onResolve({ status: 'failed', error: msg });
      }
      return;
    }

    await legacyMutation.mutateAsync({
      execution_line_item_id: data.execution_line_item_id,
      strategy_id: strategyId,
      platform_id: data.platform_id,
      account_id: data.account_id,
      entities
    });
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    if (onResolve) await onResolve({ status: 'cancelled' });
  }

  function handleToggleEdit() {
    if (editing && onFieldsEdited) {
      const diff = computeEditDiff(
        initialBudgetRef.current,
        budgetPayload,
        initialCampaignRef.current,
        campaignPayload,
        initialAdGroupRef.current,
        adGroupPayload
      );
      if (diff) onFieldsEdited(diff);
    }
    setEditing(!editing);
  }

  const applied = outcome?.status === 'applied';
  const locked = isPending || outcome !== null || Boolean(externalOutcome);
  const hasUnresolvedGeo =
    data.notices?.some(notice => notice.type === 'geo_target_unresolved') ?? false;

  const scope = isExistingCampaign
    ? 'ad group + media buy + media plan (using existing campaign)'
    : 'campaign budget + campaign + ad group + media buy + media plan';

  return (
    <>
      <CommitCardFrame
        tier="live"
        badgeLabel="Writes to Google Ads"
        title="Create media plan in Google Ads"
        consequence={`Creates ${scope} in Google Ads — created paused; nothing serves until enabled.`}
        confirmLabel="Create in Google Ads"
        confirmDisabled={editing || hasUnresolvedGeo}
        isPending={isPending}
        outcome={outcome}
        externalOutcome={externalOutcome ?? null}
        receiptUpdated={updatedAfterResolve}
        toolCallId={toolCallId}
        errorMsg={errorMsg}
        onConfirm={handleCreate}
        onCancel={handleCancel}>
        {editing ? (
          <div className="mb-3 rounded-lg bg-amber-400 px-4 py-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              Edit Mode — modify fields below, then click Done Editing
            </div>
          </div>
        ) : null}

        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Linking to: <span className="font-medium text-gray-700">{lineItemName}</span>
          </div>
          <div className="flex items-center gap-3">
            {data.account_id ? (
              <div className="text-xs text-gray-500">
                Google Ads account:{' '}
                <span className="font-medium text-gray-700">{data.account_id}</span>
              </div>
            ) : null}
            {!locked && !isMultiCampaign ? (
              <Button onClick={handleToggleEdit} variant="outline" size="sm">
                {editing ? 'Done Editing' : 'Edit'}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
          <p className="font-semibold">All entities are created PAUSED.</p>
          <p className="mt-1">
            You must add keywords + ads in the Google Ads UI before this campaign can serve.
          </p>
        </div>

        {data.notices?.map((notice, i) => {
          if (notice.type === 'start_date_adjusted') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold">Start date adjusted</p>
                <p className="mt-1">
                  The plan&apos;s start date ({notice.original_start_date}) is in the past. Google
                  Ads can&apos;t backdate serving, so this campaign will start{' '}
                  <span className="font-semibold">{notice.adjusted_start_date}</span> instead. Edit
                  the campaign start date below if you want a different date.
                </p>
              </div>
            );
          }
          if (notice.type === 'geo_target_unresolved') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-900">
                <p className="font-semibold">Geo target not recognized — creation blocked</p>
                <p className="mt-1">
                  Google Ads could not match{' '}
                  <span className="font-semibold">&quot;{notice.location_text}&quot;</span> to a
                  targetable location. This plan can&apos;t be created until every geo resolves. Ask
                  in chat with a more specific name (e.g. add the state), or ask to drop this
                  location, to regenerate the preview.
                </p>
              </div>
            );
          }
          if (notice.type === 'geo_target_ambiguous') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold">Geo target matched multiple locations</p>
                <p className="mt-1">
                  &quot;{notice.location_text}&quot; will target{' '}
                  <span className="font-semibold">{notice.resolved_display_name}</span>. Other
                  matches: {notice.alternatives.join('; ')}. If this is the wrong one, ask in chat
                  with a more specific name to regenerate this preview.
                </p>
              </div>
            );
          }
          if (notice.type === 'budget_from_pacing_block') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold">Daily budget set from the current pacing block</p>
                <p className="mt-1">
                  This line item paces in blocks. The daily budget was set to{' '}
                  <span className="font-semibold">${notice.daily_budget.toFixed(2)}/day</span> from
                  the {notice.block_from}–{notice.block_to} block. A later block has a different
                  daily amount, so update the budget in Google Ads when the next block starts.
                </p>
              </div>
            );
          }
          if (notice.type === 'daily_overspend_cap') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                <p className="font-semibold">Google Ads may overspend on a given day</p>
                <p className="mt-1">
                  Google can spend up to{' '}
                  <span className="font-semibold">${notice.max_daily_budget.toFixed(2)}/day</span>{' '}
                  (2× the ${notice.daily_budget.toFixed(2)} daily budget) on a high-traffic day,
                  balancing out to the daily average over the month. Make sure the budget accounts
                  for this.
                </p>
              </div>
            );
          }
          if (notice.type === 'account_outside_selected_mcc') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                <p className="font-semibold">Account is outside the selected manager account</p>
                <p className="mt-1">
                  Account <span className="font-semibold">{notice.account_id}</span> is not listed
                  directly under manager account{' '}
                  <span className="font-semibold">{notice.manager_account_id}</span> — it may sit
                  under a nested sub-MCC or a different manager account. It is fully usable and the
                  plan will be created in it as shown; if this is not the account you intended,
                  change the account or the manager account on the configuration card.
                </p>
              </div>
            );
          }
          if (notice.type === 'budget_mismatch') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold">Campaign budgets don&apos;t add up to the line item</p>
                <p className="mt-1">
                  The campaign budgets total{' '}
                  <span className="font-semibold">${notice.campaigns_total.toFixed(2)}</span> but
                  the line item price is{' '}
                  <span className="font-semibold">${notice.line_item_price.toFixed(2)}</span> (
                  {notice.difference >= 0 ? '+' : ''}
                  {notice.difference.toFixed(2)}). Adjust the split so they reconcile.
                </p>
              </div>
            );
          }
          if (notice.type === 'campaign_name_duplicate') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold">Campaign name already in use</p>
                <p className="mt-1">
                  A campaign named <span className="font-semibold">&quot;{notice.name}&quot;</span>{' '}
                  already exists in this Google Ads account (campaign {notice.existing_campaign_id}
                  ). Google Ads rejects duplicate campaign names, so Create will fail. Edit the
                  campaign name below to make it unique.
                </p>
              </div>
            );
          }
          if (notice.type === 'campaign_name_unverified') {
            return (
              <div
                key={`notice-${i}`}
                className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold">Campaign name not verified</p>
                <p className="mt-1">
                  We couldn&apos;t check whether{' '}
                  <span className="font-semibold">&quot;{notice.name}&quot;</span> is already in use
                  in this Google Ads account. The name is correct either way; if Create fails as a
                  duplicate, edit the campaign name below to make it unique.
                </p>
              </div>
            );
          }
          return null;
        })}

        {data.summary?.length ? <ProposalSummarySection summary={data.summary} /> : null}

        {isMultiCampaign ? (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-gray-600">
              {triples.length} campaigns — each becomes its own media plan under the line item.
            </div>
            {triples.map((triple, i) => (
              <CampaignGroupView key={`group-${i}`} index={i} triple={triple} />
            ))}
          </div>
        ) : null}

        {!isMultiCampaign && isExistingCampaign ? (
          <div className="mb-4 rounded-lg bg-gray-50 p-3">
            <h4 className="mb-2 text-xs font-semibold text-gray-500">EXISTING CAMPAIGN</h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <ViewField label="Campaign ID" value={existingCampaignId ?? ''} />
              <ViewField label="Budget ID" value={existingBudgetId ?? ''} />
            </dl>
            <p className="mt-2 text-xs text-gray-500">
              The new ad group will be added to this campaign. Its existing budget is reused — no
              new campaign or budget is created.
            </p>
          </div>
        ) : null}

        {!isMultiCampaign && budgetPayload ? (
          <BudgetSection
            payload={budgetPayload}
            onChange={setBudgetPayload}
            editing={editing}
            locked={locked}
          />
        ) : null}

        {!isMultiCampaign && campaignPayload ? (
          <CampaignSection
            payload={campaignPayload}
            onChange={setCampaignPayload}
            editing={editing}
            locked={locked}
          />
        ) : null}

        {isMultiCampaign || campaignPayload ? <AppliedAutomaticallySection /> : null}

        {!isMultiCampaign && adGroupPayload ? (
          <AdGroupSection
            payload={adGroupPayload}
            onChange={setAdGroupPayload}
            editing={editing}
            locked={locked}
          />
        ) : null}
      </CommitCardFrame>

      {applied ? (
        <div className="mt-2 space-y-2">
          {campaignSuccessLinks.length > 0 || (nextPlatform && onRequestNewPlatform) ? (
            <div className="flex flex-wrap items-center gap-3 px-1">
              {campaignSuccessLinks.map((link, i) => (
                <a
                  key={`camp-url-${i}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline">
                  {link.label} ↗
                </a>
              ))}
              {nextPlatform && onRequestNewPlatform ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRequestNewPlatform(nextPlatform.id)}>
                  Create another for {nextPlatform.name}
                </Button>
              ) : null}
            </div>
          ) : null}
          {createWarnings.length > 0 ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">Created with warnings</p>
              {createWarnings.map((warning, i) => (
                <p key={`warning-${i}`} className="mt-1">
                  {warning}
                </p>
              ))}
            </div>
          ) : null}
          {notCreated.length > 0 ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
              <p className="font-semibold">Still to set up in Google Ads</p>
              <ul className="mt-1 list-disc pl-4">
                {notCreated.map((item, i) => (
                  <li key={`not-created-${i}`} className="mt-0.5">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function findEntity(entities: EntityDecision[], entityType: string): EntityDecision | undefined {
  return entities.find(e => e.entityType === entityType);
}

function campaignLinksFromResolved(resolved: ResolvedEntity[]): LinkData[] {
  return resolved
    .filter(r => r.entityType === 'campaign')
    .map(r => ({
      url: r.url ?? buildGoogleAdsEntityUrl('campaign', r.platform_entity_id),
      label: r.entity_name ?? `Campaign ${r.platform_entity_id}`
    }));
}

type CampaignTriple = {
  budget: EntityDecision;
  campaign: EntityDecision;
  adGroup: EntityDecision;
};

// Group the flat entity list into [campaign_budget, campaign, ad_group] triples (one per campaign).
// Returns [] if the list isn't clean triples in the expected order (the backend's validateEntities is
// the source of truth) — the card then falls back to its single-campaign rendering rather than
// silently mis-rendering fields against the wrong entity.
function toTriples(entities: EntityDecision[]): CampaignTriple[] {
  if (entities.length === 0 || entities.length % 3 !== 0) return [];
  const triples: CampaignTriple[] = [];
  for (let i = 0; i + 3 <= entities.length; i += 3) {
    const budget = entities[i];
    const campaign = entities[i + 1];
    const adGroup = entities[i + 2];
    if (
      budget.entityType !== 'campaign_budget' ||
      campaign.entityType !== 'campaign' ||
      adGroup.entityType !== 'ad_group'
    ) {
      return [];
    }
    triples.push({ budget, campaign, adGroup });
  }
  return triples;
}

// Read-only view of one campaign group in a multi-campaign split. Edits to a split go back through
// chat (re-propose) so the budget shares are re-resolved against the line-item price.
function CampaignGroupView({ index, triple }: { index: number; triple: CampaignTriple }) {
  const budget =
    triple.budget.mode === 'create' ? (triple.budget.payload as CreateCampaignBudgetPayload) : null;
  const campaign =
    triple.campaign.mode === 'create' ? (triple.campaign.payload as CreateCampaignPayload) : null;
  const adGroup =
    triple.adGroup.mode === 'create' ? (triple.adGroup.payload as CreateAdGroupPayload) : null;
  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-600">Campaign {index + 1}</h4>
      {campaign ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <ViewField label="Name" value={campaign.name} />
          <ViewField label="Bidding" value={campaign.bidding_strategy} />
          {budget ? (
            <ViewField label="Daily Budget ($)" value={`$${budget.amount_dollars.toFixed(2)}`} />
          ) : null}
          <ViewField label="Start" value={campaign.start_date} />
          <ViewField label="End" value={campaign.end_date} />
          {hasGeoTargets(campaign.geo_targets) ? (
            <ViewField label="Geo" value={formatGeoTargets(campaign.geo_targets)} />
          ) : null}
          {adGroup ? (
            <ViewField
              label="Default Unit Cost (CPC) ($)"
              value={`$${adGroup.cpc_bid_dollars.toFixed(2)}`}
            />
          ) : null}
        </dl>
      ) : (
        <div className="text-sm text-gray-700">
          Existing campaign {triple.campaign.mode === 'existing' ? triple.campaign.entity_id : ''}
          {adGroup ? ` — new ad group “${adGroup.name}”` : ''}
        </div>
      )}
    </div>
  );
}

function BudgetSection({
  payload,
  onChange,
  editing,
  locked
}: {
  payload: CreateCampaignBudgetPayload;
  onChange: (p: CreateCampaignBudgetPayload) => void;
  editing: boolean;
  locked: boolean;
}) {
  if (!editing) {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-3">
        <h4 className="mb-2 text-xs font-semibold text-gray-500">CAMPAIGN BUDGET</h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <ViewField label="Name" value={payload.name} />
          <ViewField label="Daily Budget ($)" value={`$${payload.amount_dollars.toFixed(2)}`} />
          <ViewField label="Delivery" value={payload.delivery_method} />
        </dl>
      </div>
    );
  }
  return (
    <div className="mb-4 rounded-lg border p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">CAMPAIGN BUDGET</h4>
      <div className="grid grid-cols-2 gap-2">
        <EditField label="Name">
          <Input
            value={payload.name}
            onChange={e => onChange({ ...payload, name: e.target.value })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Daily Budget ($)">
          <Input
            type="number"
            step="0.01"
            value={payload.amount_dollars}
            onChange={e =>
              onChange({ ...payload, amount_dollars: parseFloat(e.target.value) || 0 })
            }
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        {/* Delivery is not editable: Google rejects anything but STANDARD on create. */}
        <EditField label="Delivery">
          <Input value={payload.delivery_method} disabled className="h-9 text-sm" />
        </EditField>
      </div>
    </div>
  );
}

function CampaignSection({
  payload,
  onChange,
  editing,
  locked
}: {
  payload: CreateCampaignPayload;
  onChange: (p: CreateCampaignPayload) => void;
  editing: boolean;
  locked: boolean;
}) {
  if (!editing) {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-3">
        <h4 className="mb-2 text-xs font-semibold text-gray-500">CAMPAIGN</h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <ViewField label="Name" value={payload.name} />
          <ViewField label="Channel Type" value={payload.advertising_channel_type} />
          <ViewField label="Bidding" value={payload.bidding_strategy} />
          {payload.bidding_strategy === 'TARGET_CPA' && payload.target_cpa_dollars !== undefined ? (
            <ViewField label="Target CPA" value={`$${payload.target_cpa_dollars}`} />
          ) : null}
          {payload.bidding_strategy === 'TARGET_ROAS' && payload.target_roas !== undefined ? (
            <ViewField label="Target ROAS" value={`${payload.target_roas}`} />
          ) : null}
          {payload.bidding_strategy === 'MAXIMIZE_CLICKS' &&
          payload.max_cpc_dollars !== undefined ? (
            <ViewField label="Max Unit Cost (CPC) ($)" value={`$${payload.max_cpc_dollars}`} />
          ) : null}
          {payload.tracking_template ? (
            <ViewField label="Tracking" value={payload.tracking_template} />
          ) : null}
          <ViewField label="Start" value={payload.start_date} />
          <ViewField label="End" value={payload.end_date} />
          {hasGeoTargets(payload.geo_targets) ? (
            <ViewField label="Geo Targeting" value={formatGeoTargets(payload.geo_targets)} />
          ) : null}
        </dl>
      </div>
    );
  }
  return (
    <div className="mb-4 rounded-lg border p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">CAMPAIGN</h4>
      <div className="grid grid-cols-2 gap-2">
        <EditField label="Name">
          <Input
            value={payload.name}
            onChange={e => onChange({ ...payload, name: e.target.value })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Bidding Strategy">
          <select
            value={payload.bidding_strategy}
            onChange={e => {
              const next = e.target.value as CreateCampaignPayload['bidding_strategy'];
              onChange({
                ...payload,
                bidding_strategy: next,
                // Drop the other strategy's target so a stale value can't be submitted.
                target_cpa_dollars: next === 'TARGET_CPA' ? payload.target_cpa_dollars : undefined,
                target_roas: next === 'TARGET_ROAS' ? payload.target_roas : undefined
              });
            }}
            disabled={locked}
            className="h-9 w-full rounded-md border px-2 text-sm leading-normal">
            <option value="MANUAL_CPC">MANUAL_CPC</option>
            <option value="MAXIMIZE_CONVERSIONS">MAXIMIZE_CONVERSIONS</option>
            <option value="TARGET_CPA">TARGET_CPA</option>
            <option value="TARGET_ROAS">TARGET_ROAS</option>
            <option value="MAXIMIZE_CLICKS">MAXIMIZE_CLICKS</option>
          </select>
        </EditField>
        {payload.bidding_strategy === 'TARGET_CPA' ? (
          <EditField label="Target CPA ($)">
            <Input
              type="number"
              value={payload.target_cpa_dollars ?? ''}
              onChange={e =>
                onChange({
                  ...payload,
                  target_cpa_dollars: e.target.value === '' ? undefined : Number(e.target.value)
                })
              }
              disabled={locked}
              className="h-9 text-sm"
            />
          </EditField>
        ) : null}
        {payload.bidding_strategy === 'TARGET_ROAS' ? (
          <EditField label="Target ROAS (ratio)">
            <Input
              type="number"
              value={payload.target_roas ?? ''}
              onChange={e =>
                onChange({
                  ...payload,
                  target_roas: e.target.value === '' ? undefined : Number(e.target.value)
                })
              }
              disabled={locked}
              className="h-9 text-sm"
            />
          </EditField>
        ) : null}
        {payload.bidding_strategy === 'MAXIMIZE_CLICKS' ? (
          <EditField label="Max Unit Cost (CPC) ($) — optional">
            <Input
              type="number"
              value={payload.max_cpc_dollars ?? ''}
              onChange={e =>
                onChange({
                  ...payload,
                  max_cpc_dollars: e.target.value === '' ? undefined : Number(e.target.value)
                })
              }
              disabled={locked}
              className="h-9 text-sm"
            />
          </EditField>
        ) : null}
        <EditField label="Tracking / UTM — optional">
          <Input
            value={payload.tracking_template ?? ''}
            onChange={e =>
              onChange({
                ...payload,
                tracking_template: e.target.value === '' ? undefined : e.target.value
              })
            }
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Start Date">
          <Input
            value={payload.start_date}
            onChange={e => onChange({ ...payload, start_date: e.target.value })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="End Date">
          <Input
            value={payload.end_date}
            onChange={e => onChange({ ...payload, end_date: e.target.value })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        {/* Geo is not editable here: changes go through chat (re-propose) so named locations are
            re-resolved to Google geo target constants at preview time. */}
        {hasGeoTargets(payload.geo_targets) ? (
          <EditField label="Geo Targeting (edit via chat)">
            <Input value={formatGeoTargets(payload.geo_targets)} disabled className="h-9 text-sm" />
          </EditField>
        ) : null}
      </div>
    </div>
  );
}

function AdGroupSection({
  payload,
  onChange,
  editing,
  locked
}: {
  payload: CreateAdGroupPayload;
  onChange: (p: CreateAdGroupPayload) => void;
  editing: boolean;
  locked: boolean;
}) {
  if (!editing) {
    return (
      <div className="rounded-lg bg-gray-50 p-3">
        <h4 className="mb-2 text-xs font-semibold text-gray-500">AD GROUP</h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <ViewField label="Name" value={payload.name} />
          <ViewField label="Type" value={payload.type} />
          <ViewField
            label="Default Unit Cost (CPC) ($)"
            value={`$${payload.cpc_bid_dollars.toFixed(2)}`}
          />
        </dl>
      </div>
    );
  }
  return (
    <div className="rounded-lg border p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">AD GROUP</h4>
      <div className="grid grid-cols-2 gap-2">
        <EditField label="Name">
          <Input
            value={payload.name}
            onChange={e => onChange({ ...payload, name: e.target.value })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Default Unit Cost (CPC) ($)">
          <Input
            type="number"
            step="0.01"
            value={payload.cpc_bid_dollars}
            onChange={e =>
              onChange({ ...payload, cpc_bid_dollars: parseFloat(e.target.value) || 0 })
            }
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
      </div>
    </div>
  );
}

// Read-only display of the hardcoded trader-spec invariants (set server-side, not editable). Shown
// so the user confirms with eyes open what is applied to every new campaign. EU political ads is
// called out as immutable-after-publish.
function AppliedAutomaticallySection() {
  return (
    <div className="mb-4 rounded-lg bg-gray-50 p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">APPLIED AUTOMATICALLY</h4>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <ViewField label="Networks" value="Google Search only" />
        <ViewField label="Location matching" value="Presence only" />
        <ViewField label="Language" value="English" />
        <ViewField label="AI Max" value="Off" />
        <ViewField label="EU political ads" value="Not declared (cannot change after publish)" />
      </dl>
      <p className="mt-2 text-xs text-gray-500">
        EU political ads default to “Not declared” and cannot be changed after publish. A specialist
        must manually confirm the correct declaration before launch.
      </p>
    </div>
  );
}

function hasGeoTargets(geo: CampaignGeoTargets | undefined): geo is CampaignGeoTargets {
  return Boolean(geo?.locations?.length || geo?.proximities?.length);
}

function formatGeoTargets(geo: CampaignGeoTargets): string {
  const parts: string[] = [];
  for (const location of geo.locations ?? []) {
    parts.push(
      location.resolved_resource_name
        ? (location.resolved_display_name ?? location.location_text)
        : `${location.location_text} (unresolved)`
    );
  }
  for (const proximity of geo.proximities ?? []) {
    parts.push(`${proximity.radius_miles} mi around ${proximity.address_text}`);
  }
  return parts.join('; ');
}

function ViewField({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900 wrap-break-word">{value}</dd>
    </>
  );
}

function EditField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-0.5 block text-xs text-gray-500">{label}</label>
      {children}
    </div>
  );
}

function computeEditDiff(
  initialBudget: CreateCampaignBudgetPayload | null,
  currentBudget: CreateCampaignBudgetPayload | null,
  initialCampaign: CreateCampaignPayload | null,
  currentCampaign: CreateCampaignPayload | null,
  initialAdGroup: CreateAdGroupPayload | null,
  currentAdGroup: CreateAdGroupPayload | null
): string | null {
  const changes: string[] = [];

  if (initialBudget && currentBudget) {
    if (initialBudget.name !== currentBudget.name)
      changes.push(`budget name to "${currentBudget.name}"`);
    if (initialBudget.amount_dollars !== currentBudget.amount_dollars)
      changes.push(`daily budget to $${currentBudget.amount_dollars}`);
  }
  if (initialCampaign && currentCampaign) {
    if (initialCampaign.name !== currentCampaign.name)
      changes.push(`campaign name to "${currentCampaign.name}"`);
    if (initialCampaign.bidding_strategy !== currentCampaign.bidding_strategy)
      changes.push(`bidding strategy to ${currentCampaign.bidding_strategy}`);
    if (initialCampaign.target_cpa_dollars !== currentCampaign.target_cpa_dollars)
      changes.push(`target CPA to $${currentCampaign.target_cpa_dollars}`);
    if (initialCampaign.target_roas !== currentCampaign.target_roas)
      changes.push(`target ROAS to ${currentCampaign.target_roas}`);
    if (initialCampaign.max_cpc_dollars !== currentCampaign.max_cpc_dollars)
      changes.push(`max CPC to $${currentCampaign.max_cpc_dollars}`);
    if (initialCampaign.tracking_template !== currentCampaign.tracking_template)
      changes.push(`tracking to "${currentCampaign.tracking_template}"`);
    if (initialCampaign.start_date !== currentCampaign.start_date)
      changes.push(`start date to ${currentCampaign.start_date}`);
    if (initialCampaign.end_date !== currentCampaign.end_date)
      changes.push(`end date to ${currentCampaign.end_date}`);
  }
  if (initialAdGroup && currentAdGroup) {
    if (initialAdGroup.name !== currentAdGroup.name)
      changes.push(`ad group name to "${currentAdGroup.name}"`);
    if (initialAdGroup.cpc_bid_dollars !== currentAdGroup.cpc_bid_dollars)
      changes.push(`CPC bid to $${currentAdGroup.cpc_bid_dollars}`);
  }
  if (changes.length === 0) return null;
  return `Changed ${changes.join(', ')}`;
}
